// System-account detail (SM-152) — balances + operator actions
// (freeze/unfreeze/close, transfer, fund user, mint/burn), children,
// the account's movement history, and its slice of the audit log.
// Mirrors treasury.$id.tsx in shape (2-col Cards grid, per-panel
// pagination at HISTORY_PAGE).
//
// The virtual PLATFORM_REVENUE root renders read-only: it has no
// system_accounts row upstream — the only verb it supports is being a
// transfer SOURCE (Allocate revenue). Every mutation is perm-gated
// per-verb (the seed-0019 SYSTEM_ACCOUNT_* codes), so the page ships
// dark for roles without the grants.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  closeSystemAccount,
  fetchAudit,
  fetchCampaignFundingOptions,
  fetchCampaigns,
  fetchSystemAccount,
  fetchSystemAccounts,
  fetchSystemAccountTransactions,
  freezeSystemAccount,
  keys,
  unfreezeSystemAccount,
} from "@/lib/queries";
import type { Campaign } from "@/lib/queries";
import type { SystemAccount, WalletTransaction } from "@/lib/types";
import { campaignCurrency, isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import { SystemAccountAdjustmentCard } from "@/components/treasury/SystemAccountAdjustmentCard";
import { SystemAccountCryptoCard } from "@/components/treasury/SystemAccountCryptoCard";
import { SystemAccountTransferDialog } from "@/components/treasury/SystemAccountTransferDialog";
import { AllocateRevenueDialog } from "@/components/treasury/AllocateRevenueDialog";
import { CreateChildAccountDialog } from "@/components/treasury/CreateChildAccountDialog";
import { FundUserDialog } from "@/components/treasury/FundUserDialog";
import { LinkedMMCard } from "@/components/treasury/LinkedMMCard";
import { MovementRoute } from "@/components/treasury/MovementRoute";
import { accountStatusTone, accountTotal } from "@/components/treasury/SystemAccountTree";
import {
  fundingHealth,
  fundingHealthLabel,
  isFundingProblem,
} from "@/components/campaigns/fundingHealth";
import { humanizeCloseAccountError, humanizeError } from "@/lib/errorMessages";
import { TechHint } from "@/components/ui/TechHint";
import { fmtRelative, isCryptoTxKind, statusLabel, txKindLabel } from "@/lib/format";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";

type PendingAction = "freeze" | "unfreeze" | "close";
type OpenDialog = "transfer" | "fund" | "child" | "allocate" | null;
// The two ways money enters or leaves a system account (SM-335).
type MoneyPane = "onchain" | "manual";

// Each history panel paginates at this slice size — same rationale as
// the wallet detail page (fixed compact card height).
const HISTORY_PAGE = 10;

// Campaigns card slice — newest first; the header total says when more
// exist than we show.
const CAMPAIGNS_PAGE = 50;

export const Route = createFileRoute("/_app/treasury/accounts/$code")({
  // The code alone does not identify an account: since ledger migration 0025
  // there is one row per (account_code, currency), and seventeen codes on SIT
  // have two or three.
  //
  // OPTIONAL in the URL, resolved in the component. A link that knows which
  // row it means should say so; one that does not gets GOLD, exactly as the
  // API does for an absent ?currency=. Making it required in the type would
  // force every existing link in the console to restate a default they
  // already have — the type would be documenting the router, not the domain.
  validateSearch: (search: Record<string, unknown>): { currency?: CurrencyCode } =>
    typeof search.currency === "string" && isCurrencyCode(search.currency)
      ? { currency: search.currency }
      : {},
  component: SystemAccountDetailPage,
});

function SystemAccountDetailPage() {
  const { code } = Route.useParams();
  const currency = campaignCurrency(Route.useSearch().currency);
  const currencyName = useCurrencyName(currency);
  const goldName = useCurrencyName("GOLD");
  const qc = useQueryClient();
  const perms = useMyPerms();

  const account = useQuery({
    queryKey: keys.systemAccountDetail(code, currency),
    queryFn: () => fetchSystemAccount(code, currency),
  });
  // Whole tree — children of this node + transfer destinations.
  const accounts = useQuery({
    queryKey: keys.systemAccounts(),
    queryFn: fetchSystemAccounts,
  });

  const [txnPage, setTxnPage] = useState(0);
  const transactions = useQuery({
    queryKey: keys.systemAccountTxns(code, currency, HISTORY_PAGE, txnPage * HISTORY_PAGE),
    queryFn: () =>
      fetchSystemAccountTransactions(code, currency, {
        limit: HISTORY_PAGE,
        offset: txnPage * HISTORY_PAGE,
      }),
  });

  // The account's slice of the operator audit log (SM-152 target
  // filters). Server-gated by ADMIN_VIEW_AUDIT_LOG, so only ask when
  // the role carries it — otherwise the card is hidden entirely.
  const canSeeAudit = perms.has(Perm.AdminViewAuditLog);
  const auditFilters = { target_kind: "system_account", target_id: code };
  const audit = useQuery({
    queryKey: keys.auditList(HISTORY_PAGE, 0, undefined, auditFilters),
    queryFn: () => fetchAudit({ limit: HISTORY_PAGE, offset: 0, ...auditFilters }),
    enabled: canSeeAudit,
  });

  // Campaigns drawing on this account (SM-195: a new campaign was
  // invisible here). PROMOTION-kind accounts only, capability-probed the
  // same way as the campaign form — fundingOptions resolves null while
  // transfer funding is dark, and the whole card disappears with it.
  const canSeeCampaigns = perms.has(Perm.CampaignView);
  const isPromotionKind = account.data?.account_kind === "PROMOTION";
  const fundingOpts = useQuery({
    queryKey: keys.campaignFundingOptions(),
    queryFn: fetchCampaignFundingOptions,
    staleTime: 60_000,
    enabled: canSeeCampaigns && isPromotionKind,
  });
  const fundedCampaigns = useQuery({
    queryKey: keys.campaignsByFunding(code),
    queryFn: () =>
      fetchCampaigns({
        limit: CAMPAIGNS_PAGE,
        offset: 0,
        funding_account_code: code,
        sort: { by: "created_at", dir: "desc" },
      }),
    enabled: canSeeCampaigns && isPromotionKind && !!fundingOpts.data,
  });

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [dialog, setDialog] = useState<OpenDialog>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: keys.systemAccounts() });
    qc.invalidateQueries({ queryKey: keys.systemAccountDetail(code, currency) });
    qc.invalidateQueries({
      queryKey: keys.systemAccountTxns(code, currency, HISTORY_PAGE, txnPage * HISTORY_PAGE),
    });
    qc.invalidateQueries({ queryKey: keys.audit });
  };

  const freeze = useMutation({
    mutationFn: (reason: string) => freezeSystemAccount(code, currency, reason),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Account frozen." },
  });
  const unfreeze = useMutation({
    mutationFn: (reason: string) => unfreezeSystemAccount(code, currency, reason),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Account unfrozen." },
  });
  const close = useMutation({
    mutationFn: (reason: string) => closeSystemAccount(code, currency, reason),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Account closed." },
  });

  const closeModal = () => {
    freeze.reset();
    unfreeze.reset();
    close.reset();
    setPending(null);
  };

  const a = account.data;
  const children = (accounts.data ?? []).filter(
    // The PAIR, not the code: a child belongs to the parent row that shares
    // its currency. Filtering on the code alone listed all three currency
    // variants of every child under whichever row was being viewed.
    (c) => c.parent_account_code === code && campaignCurrency(c.currency) === currency,
  );
  // The same code's GOLD row, when it has one — the rail lives there.
  const goldRow = (accounts.data ?? []).find(
    (c) => c.account_code === code && campaignCurrency(c.currency) === "GOLD",
  );
  const isActive = a?.status === "ACTIVE";
  const isVirtual = !!a?.virtual;
  const isRoot = !a?.parent_account_code;
  // The same total the card shows, so the Close guard and the number an
  // operator reads to satisfy it can never disagree.
  const hasBalance = !!a && accountTotal(a) !== "0";

  const canDeposit = perms.has(Perm.SystemAccountDeposit);
  const canWithdraw = perms.has(Perm.SystemAccountWithdraw);

  // A SYSTEM_MM child is a market-maker funding reserve (SM-342). Only
  // children qualify — the SYSTEM_MM root funds nothing in particular,
  // and generic TREASURY / PROMOTION / REWARDS pages must be untouched.
  const isMakerReserve = a?.account_kind === "MM" && !!a?.parent_account_code && !isVirtual;

  // Money movement (SM-335): the account has two money paths and they
  // are not equivalent. On-chain is the real one — it moves value across
  // the platform boundary — so it is the default.
  //
  // Manual mint/burn stays available as the production fallback for when
  // the rail is down or an account needs correcting, but it is gated to
  // SUPER_ADMIN in the UI on an explicit operator instruction: it
  // fabricates platform money from nothing, and the set of people who
  // should reach for it is smaller than the set holding
  // SYSTEM_ACCOUNT_DEPOSIT. This is a UI-only narrowing — the server's
  // permissions are unchanged, and mcp-service / a direct API call still
  // honour the SYSTEM_ACCOUNT_* grants alone.
  // isSuperAdmin is read DIRECTLY rather than through perms.has(), which
  // returns true for a super admin on every code — the point here is the
  // role itself, not a capability anyone else can be granted.
  const canUseManual = perms.isSuperAdmin;
  // Every crypto route is an intersection with CRYPTO_WALLET_VIEW
  // server-side; without it the pane would render five 403s.
  //
  // …and the rail itself is GOLD-only (SM-500). Its five routes carry no
  // currency, so the ledger resolves them against the GOLD row: opened
  // on RED or GREEN, the pane showed GOLD's deposit address, GOLD's
  // orders, and would have held GOLD's balance for a withdrawal — an
  // action on an account the operator was not looking at. That is also
  // the correct policy and not just the current plumbing: value leaves
  // the platform as GOLD (RED is never withdrawn, GREEN converts to GOLD
  // first), so there is no on-chain rail for the other two to be missing.
  const isRailCurrency = currency === "GOLD";
  const canSeeCrypto = perms.has(Perm.CryptoWalletView) && isRailCurrency;
  const showMovementPanes = canUseManual && canSeeCrypto;
  const [pane, setPane] = useState<MoneyPane>("onchain");
  // A super admin with no crypto grant would otherwise land on an
  // on-chain pane they can't read. Nobody else is ever moved to manual:
  // the fallback below refuses to render it without canUseManual, so a
  // non-super-admin missing CRYPTO_WALLET_VIEW gets an explanation
  // rather than the mint/burn form.
  useEffect(() => {
    if (canUseManual && !canSeeCrypto && pane === "onchain") setPane("manual");
    if (!canUseManual && pane === "manual") setPane("onchain");
  }, [canSeeCrypto, canUseManual, pane]);
  const showManualPane = canUseManual && pane === "manual";

  return (
    <div className="space-y-6">
      <div>
        {/* Carries the currency so "back" lands on the list the operator
            left, not on GOLD's (SM-500). */}
        <Link
          to="/treasury/accounts"
          search={{ currency }}
          className="text-2xs text-muted hover:text-fg"
        >
          ← back to system accounts
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="font-display text-2xl tracking-tight">
            {a?.account_name ?? code}
          </h1>
          {/* Which of the code's rows this page is showing. Three pages
              share a name and differ only here and in the numbers. */}
          <CurrencyBadge currency={currency} />
          {isVirtual && <Badge tone="chrome">virtual</Badge>}
        </div>
        <p className="font-mono text-2xs text-muted">{code}</p>
      </div>

      {account.isLoading && <Skeleton className="h-32" />}
      {account.isError && (
        <ErrorPanel error={account.error} reset={() => account.refetch()} />
      )}
      {a && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>Account</CardTitle>
              <Badge tone={accountStatusTone(a.status)}>{statusLabel(a.status)}</Badge>
            </CardHeader>
            <CardBody className="space-y-2 pt-5">
              <Field k="Code" v={<span className="font-mono text-2xs">{a.account_code}</span>} />
              <Field k="Kind" v={<Badge tone="muted">{a.account_kind}</Badge>} />
              {a.parent_account_code && (
                <Field
                  k="Parent"
                  v={
                    <Link
                      to="/treasury/accounts/$code"
                      params={{ code: a.parent_account_code }}
                      // A child belongs to the parent ROW sharing its
                      // currency, so the parent link means THAT row. Without
                      // this, clicking up from RED's promotion account landed
                      // on GOLD's promotion root (SM-500).
                      search={{ currency }}
                      className="font-mono text-2xs text-neon hover:text-neon-2"
                    >
                      {a.parent_account_code}
                    </Link>
                  }
                />
              )}
              {a.purpose && <Field k="Purpose" v={a.purpose} />}
              {/* On an MM child this balance is a funding RESERVE, not
                  a tradable balance — label it as such so it can never
                  be read as the same money as the execution wallet
                  shown below (SM-342). Non-MM accounts keep "Available". */}
              <Field
                k={isMakerReserve ? "Funding reserve" : "Available"}
                v={<span className="num font-medium"><CurrencyAmount currency={currency} value={a.available} /></span>}
              />
              <Field
                k="Reserved"
                v={<span className="num font-medium"><CurrencyAmount currency={currency} value={a.reserved} tone="warning" /></span>}
              />
              <Field k="Pending W/D" v={<CurrencyAmount currency={currency} value={a.pending_withdrawal} />} />
              {/* Available + reserved + pending: what this row holds in
                  total, so "is it drained?" is one number rather than
                  three to add — and it is the number the Close guard
                  below actually tests. */}
              <Field
                k="Total"
                v={
                  <span className="num font-medium">
                    <CurrencyAmount currency={currency} value={accountTotal(a)} />
                  </span>
                }
              />
              <Field k="Updated" v={fmtRelative(a.updated_at)} />

              <div className="flex flex-wrap items-center gap-1 pt-3">
                {isVirtual ? (
                  // The revenue root's single verb: allocate out of it.
                  perms.has(Perm.SystemAccountAllocateRevenue) && (
                    <Button size="sm" onClick={() => setDialog("allocate")}>
                      Allocate revenue
                    </Button>
                  )
                ) : (
                  <>
                    {perms.has(Perm.SystemAccountTransfer) && a.status !== "CLOSED" && (
                      <Button size="sm" variant="ghost" onClick={() => setDialog("transfer")}>
                        Transfer
                      </Button>
                    )}
                    {perms.has(Perm.SystemAccountFundUser) && a.status !== "CLOSED" && (
                      <Button size="sm" variant="ghost" onClick={() => setDialog("fund")}>
                        Fund user
                      </Button>
                    )}
                    {perms.has(Perm.SystemAccountUpdate) && a.status !== "CLOSED" && (
                      isActive ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-danger"
                          onClick={() => setPending("freeze")}
                        >
                          Freeze
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setPending("unfreeze")}>
                          Unfreeze
                        </Button>
                      )
                    )}
                    {perms.has(Perm.SystemAccountUpdate) && !isRoot && a.status !== "CLOSED" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger"
                        onClick={() => setPending("close")}
                      >
                        Close
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardBody>
          </Card>

          {isMakerReserve && <LinkedMMCard account={a} />}

          {!isVirtual && (canDeposit || canWithdraw) && (
            <Card className="lg:col-span-2">
              <CardHeader className="border-b border-border">
                <CardTitle>Money movement</CardTitle>
                {/* The control only appears for an operator who has both
                    panes. For everyone else there is nothing to switch
                    between, and a one-option segmented control reads as
                    a disabled feature rather than the only feature. */}
                {showMovementPanes && (
                  <SegmentedControl<MoneyPane>
                    options={[
                      { value: "onchain", label: "On-chain" },
                      { value: "manual", label: "Manual adjustment" },
                    ]}
                    value={pane}
                    onChange={setPane}
                  />
                )}
              </CardHeader>
              <CardBody className="p-0">
                {showManualPane ? (
                  // The adjustment card ships its own Card chrome; strip
                  // it so the pane reads as part of THIS card instead of
                  // a box inside a box. The component itself is
                  // untouched — SM-152 owns it.
                  <div className="[&>.card]:rounded-none [&>.card]:border-0 [&>.card]:bg-transparent [&>.card]:p-0">
                    <SystemAccountAdjustmentCard
                      accountCode={a.account_code}
                      accountCurrency={currency}
                      available={a.available}
                      isActive={isActive}
                      status={a.status}
                      canDeposit={canDeposit}
                      canWithdraw={canWithdraw}
                      onAdjusted={invalidate}
                    />
                  </div>
                ) : canSeeCrypto ? (
                  <SystemAccountCryptoCard
                    accountCode={a.account_code}
                    isActive={isActive}
                    status={a.status}
                    canDeposit={canDeposit}
                    canWithdraw={canWithdraw}
                    onChanged={invalidate}
                  />
                ) : !isRailCurrency ? (
                  <p className="px-5 py-6 text-2xs text-muted">
                    {/* One sentence per translation unit. Written as
                        template literals because the extractor splits a
                        JSX text node at every {expr}, and a link in the
                        middle of prose splits it again — which is how a
                        catalogue ends up holding ", or open" as a phrase
                        somebody is expected to translate. */}
                    {`${currencyName} does not move on-chain — value enters and leaves the platform as ${goldName}. Mint or burn this balance with a manual adjustment.`}
                    {goldRow ? (
                      <>
                        {" "}
                        <Link
                          to="/treasury/accounts/$code"
                          params={{ code }}
                          search={{ currency: "GOLD" }}
                          className="text-neon hover:text-neon-2"
                        >
                          {`Open this account's ${goldName} row`}
                        </Link>
                        {` to reach the crypto rail.`}
                      </>
                    ) : null}
                  </p>
                ) : (
                  <p className="px-5 py-6 text-2xs text-muted">
                    Moving this account's money on-chain needs the
                    CRYPTO_WALLET_VIEW permission alongside your treasury
                    grant.
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {!isVirtual && (
            <Card className="lg:col-span-2">
              <CardHeader className="border-b border-border">
                <CardTitle>Children</CardTitle>
                <div className="flex items-center gap-3">
                  <span className="text-2xs text-muted">{children.length}</span>
                  {perms.has(Perm.SystemAccountCreate) && a.status !== "CLOSED" && (
                    <Button size="sm" variant="ghost" onClick={() => setDialog("child")}>
                      Create child account
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <ChildrenTable rows={children} loading={accounts.isLoading} />
              </CardBody>
            </Card>
          )}

          {canSeeCampaigns && isPromotionKind && fundingOpts.data && (
            <Card className="lg:col-span-2">
              <CardHeader className="border-b border-border">
                <CardTitle>Campaigns funded by this account</CardTitle>
                <div className="flex items-center gap-3">
                  <span className="text-2xs text-muted">
                    {fundedCampaigns.data?.total ?? 0}
                    {(fundedCampaigns.data?.total ?? 0) > CAMPAIGNS_PAGE &&
                      ` — showing newest ${CAMPAIGNS_PAGE}`}
                  </span>
                  <Link to="/campaigns" className="text-2xs text-muted hover:text-fg">
                    all campaigns →
                  </Link>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <FundedCampaignsTable
                  account={a}
                  rows={fundedCampaigns.data?.items ?? []}
                  loading={fundedCampaigns.isLoading}
                />
              </CardBody>
            </Card>
          )}

          <Card className="lg:col-span-2">
            <CardHeader className="border-b border-border">
              <CardTitle>Movements</CardTitle>
              <div className="flex items-center gap-2">
                {/* Which account's history this is. The table below holds
                    one currency only and says so once, here. */}
                <CurrencyBadge currency={currency} />
                <span className="text-2xs text-muted">
                  {transactions.data?.page.total ?? 0}
                </span>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              <MovementsTable
                rows={transactions.data?.items ?? []}
                currency={currency}
                loading={transactions.isLoading}
              />
              {transactions.data && transactions.data.page.total > HISTORY_PAGE && (
                <Pagination
                  page={transactions.data.page}
                  currentPage={txnPage}
                  onPageChange={setTxnPage}
                  itemLabel="movements"
                />
              )}
            </CardBody>
          </Card>

          {canSeeAudit && (
            <Card className="lg:col-span-2">
              <CardHeader className="border-b border-border">
                <CardTitle>Audit</CardTitle>
                <Link to="/audit" className="text-2xs text-muted hover:text-fg">
                  full log →
                </Link>
              </CardHeader>
              <CardBody className="p-0">
                {audit.isLoading && (
                  <div className="space-y-2 p-5">
                    {[0, 1].map((i) => (
                      <Skeleton key={i} className="h-6" />
                    ))}
                  </div>
                )}
                {audit.isError && (
                  <p className="px-5 py-6 text-2xs text-muted">
                    Couldn't load the audit slice — {humanizeError(audit.error)}
                  </p>
                )}
                {audit.data &&
                  (audit.data.items.length === 0 ? (
                    <p className="px-5 py-6 text-2xs text-muted">
                      no operator actions recorded for this account yet
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-2xs">
                        <thead className="border-b border-border bg-surface-2 text-muted">
                          <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Event</th>
                            <th className="px-3 py-2 text-left">Actor</th>
                            <th className="px-3 py-2 text-left">Summary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audit.data.items.map((e) => (
                            <tr key={e.event_id} className="border-b border-border/60 last:border-0">
                              <td className="whitespace-nowrap px-3 py-1.5 text-muted">
                                {fmtRelative(e.occurred_at)}
                              </td>
                              <td className="px-3 py-1.5">
                                <Badge
                                  tone={
                                    e.severity === "CRITICAL" || e.severity === "ERROR"
                                      ? "danger"
                                      : e.severity === "WARN"
                                        ? "warning"
                                        : "muted"
                                  }
                                >
                                  {e.event_type}
                                </Badge>
                              </td>
                              <td className="px-3 py-1.5 text-muted">
                                {e.actor_email || e.admin_id || "—"}
                              </td>
                              <td className="max-w-[40ch] truncate px-3 py-1.5 text-muted" title={e.summary || ""}>
                                {e.summary || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
              </CardBody>
            </Card>
          )}
        </div>
      )}

      <ConfirmModal
        open={pending === "freeze"}
        tone="danger"
        title="Freeze this account?"
        description={
          <>
            <p>
              The operator kill-switch: outflows from{" "}
              <span className="font-mono text-2xs text-t-2">{code}</span> stop
              immediately (promotion payouts / MM funding drawing on it start
              failing safe), while deposits still land.
            </p>
            <p className="mt-2 text-2xs">Required reason lands in the audit log alongside the operator id.</p>
          </>
        }
        confirmLabel="Freeze account"
        cancelLabel="Cancel"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={freeze.isPending}
        error={freeze.isError ? humanizeError(freeze.error) : null}
        onConfirm={(r) => freeze.mutate(r)}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "unfreeze"}
        tone="primary"
        title="Unfreeze this account?"
        description={
          <p>
            Restores outflows from{" "}
            <span className="font-mono text-2xs text-t-2">{code}</span> — transfers,
            promotion payouts, and MM funding resume.
          </p>
        }
        confirmLabel="Unfreeze account"
        cancelLabel="Cancel"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={unfreeze.isPending}
        error={unfreeze.isError ? humanizeError(unfreeze.error) : null}
        onConfirm={(r) => unfreeze.mutate(r)}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={pending === "close"}
        tone="danger"
        title="Close this account?"
        description={
          <>
            <p>
              Closing is terminal — a closed account can never move money again.
              The server only accepts a fully drained account.
            </p>
            {hasBalance && (
              <p className="mt-2 text-2xs text-warning">
                This account still holds balance — withdraw or transfer the
                remainder first.
              </p>
            )}
          </>
        }
        confirmLabel="Close account"
        cancelLabel="Cancel"
        reasonLabel="Reason (audit log)"
        reasonRequired
        confirmDisabled={hasBalance}
        loading={close.isPending}
        error={close.isError ? humanizeCloseAccountError(close.error) : null}
        onConfirm={(r) => close.mutate(r)}
        onCancel={closeModal}
      />

      {a && (
        <>
          {/* Keyed by the ROW, so changing account or currency gives each
              form a clean instance rather than one still holding inputs
              validated against the balance that was on screen before. The
              dialogs reset on their own too; the key is the belt to that
              braces, and it is what makes "changing the currency
              revalidates the form" true by construction. */}
          <SystemAccountTransferDialog
            key={`transfer:${a.wallet_id}`}
            open={dialog === "transfer"}
            source={a}
            accounts={accounts.data ?? []}
            onClose={() => setDialog(null)}
          />
          <FundUserDialog
            key={`fund:${a.wallet_id}`}
            open={dialog === "fund"}
            source={a}
            onClose={() => setDialog(null)}
          />
          <CreateChildAccountDialog
            key={`child:${a.wallet_id}`}
            open={dialog === "child"}
            parent={a}
            onClose={() => setDialog(null)}
          />
          <AllocateRevenueDialog
            key={`allocate:${a.wallet_id}`}
            open={dialog === "allocate"}
            accounts={accounts.data ?? []}
            currency={currency}
            onClose={() => setDialog(null)}
          />
        </>
      )}
    </div>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-muted">{k}</span>
      <span className="text-right text-fg">{v}</span>
    </div>
  );
}

function ChildrenTable({
  rows,
  loading,
}: {
  rows: SystemAccount[];
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="space-y-2 p-5">
        {[0, 1].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  if (rows.length === 0)
    return (
      <p className="px-5 py-6 text-2xs text-muted">
        none — create a child to give a campaign / run / program its own budget
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-right">Available</th>
            <th className="px-3 py-2 text-right">Reserved</th>
            <th className="px-3 py-2 text-right">Pending</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.wallet_id} className="border-b border-border/60 last:border-0">
              <td className="px-3 py-1.5">
                <Link
                  to="/treasury/accounts/$code"
                  params={{ code: c.account_code }}
                  search={{ currency: campaignCurrency(c.currency) }}
                  className="block leading-tight hover:text-accent"
                >
                  <span className="block text-xs font-medium text-fg">{c.account_name}</span>
                  <span className="font-mono text-2xs text-muted">{c.account_code}</span>
                </Link>
              </td>
              <td className="px-3 py-1.5 text-right font-mono">
                <CurrencyAmount currency={c.currency} value={c.available} />
              </td>
              <td className="px-3 py-1.5 text-right font-mono">
                <CurrencyAmount currency={c.currency} value={c.reserved} tone="warning" />
              </td>
              <td className="px-3 py-1.5 text-right font-mono">
                <CurrencyAmount
                  currency={c.currency}
                  value={c.pending_withdrawal}
                  tone="muted"
                />
              </td>
              <td className="px-3 py-1.5 text-right font-mono font-medium">
                <CurrencyAmount currency={c.currency} value={accountTotal(c)} />
              </td>
              <td className="px-3 py-1.5">
                <Badge tone={accountStatusTone(c.status)}>{statusLabel(c.status)}</Badge>
              </td>
              <td className="px-3 py-1.5 text-right text-muted">{fmtRelative(c.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Every row's effective funding source IS this page's account (the
// server filter matches explicit overrides plus default-funded rows on
// the default account), so health checks run against the live account
// row — fresher than the funding-options snapshot.
function FundedCampaignsTable({
  account,
  rows,
  loading,
}: {
  account: SystemAccount;
  rows: Campaign[];
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="space-y-2 p-5">
        {[0, 1].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  if (rows.length === 0)
    return (
      <p className="px-5 py-6 text-2xs text-muted">
        no campaigns draw on this account yet — pick it under Funding when
        creating a campaign
      </p>
    );
  const opts = {
    enabled: true,
    default_account_code: account.account_code,
    accounts: [
      {
        account_code: account.account_code,
        account_name: account.account_name,
        available: account.available,
        status: account.status,
        // (account_code, currency) is the ledger identity, and
        // fundingHealth resolves on the PAIR — a row without the
        // currency reads as GOLD, so every campaign funded by a RED or
        // GREEN account came back "Funding currency mismatch" against
        // the very account funding it (SM-500).
        currency: account.currency,
      },
    ],
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">Campaign</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Delivery</th>
            <th className="px-3 py-2 text-right">Reward</th>
            <th className="px-3 py-2 text-right">Credited</th>
            <th className="px-3 py-2 text-left">Funding</th>
            <th className="px-3 py-2 text-right">Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const health = c.status === "DISABLED" ? null : fundingHealth(c, opts);
            return (
              <tr key={c.campaign_id} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5">
                  <Link
                    to="/campaigns/$id"
                    params={{ id: c.campaign_id }}
                    className="text-xs font-medium text-fg hover:text-accent"
                  >
                    {c.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <Badge
                    tone={
                      c.status === "ACTIVE"
                        ? "success"
                        : c.status === "DRAFT"
                          ? "warning"
                          : "muted"
                    }
                  >
                    {c.status}
                  </Badge>
                </td>
                <td className="px-3 py-1.5">
                  <Badge tone={c.delivery === "CLAIM" ? "accent" : "muted"}>{c.delivery}</Badge>
                </td>
                {/* The CAMPAIGN's reward currency (SM-436), not the
                    page's: a campaign is funded by this account and pays
                    in its own currency, and the two are the same only
                    when funding is healthy — which is what the column
                    beside this one is for. */}
                <td className="px-3 py-1.5 text-right font-mono">
                  <CurrencyAmount currency={c.currency} value={c.reward_amount_minor} />
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  <CurrencyAmount currency={c.currency} value={c.credited_total_minor} />
                </td>
                <td className="px-3 py-1.5">
                  <span className="flex items-center gap-1.5">
                    {!c.funding_account_code && (
                      <span className="text-muted" title="No override — uses the platform default account">
                        default
                      </span>
                    )}
                    {health && isFundingProblem(health) && (
                      <Badge tone={health.kind === "underfunded" ? "warning" : "danger"}>
                        {fundingHealthLabel(health.kind)}
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right text-muted">{fmtRelative(c.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Every row here is a movement on ONE account row, so every row is in
// that row's currency — the query is keyed by (code, currency). Drawing
// them with <SparkAmount> printed GOLD's mark and GOLD's precision over
// RED and GREEN movements, which is the "never merge nominal amounts
// across currencies" rule broken at the point it matters most: the
// history an operator reconciles against the ledger (SM-500).
function MovementsTable({
  rows,
  currency,
  loading,
}: {
  rows: WalletTransaction[];
  currency: CurrencyCode;
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="space-y-2 p-5">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  if (rows.length === 0)
    return <p className="px-5 py-6 text-2xs text-muted">no movements yet</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-2xs">
        <thead className="border-b border-border bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left">When</th>
            <th className="px-3 py-2 text-left">Kind</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Route</th>
            <th className="px-3 py-2 text-left">Reason</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.transaction_id} className="border-b border-border/60 last:border-0">
              <td className="whitespace-nowrap px-3 py-1.5 text-muted">
                {fmtRelative(t.created_at)}
              </td>
              {/* Accent marks the kinds that crossed the platform
                  boundary on a chain, so an on-chain row never reads
                  like the mint/burn next to it. Raw enum on hover. */}
              <td className="px-3 py-1.5">
                <TechHint tech={t.kind}>
                  <Badge tone={isCryptoTxKind(t.kind) ? "accent" : "muted"}>
                    {txKindLabel(t.kind)}
                  </Badge>
                </TechHint>
              </td>
              <td className="px-3 py-1.5 text-right font-mono">
                <CurrencyAmount
                  currency={currency}
                  value={t.display_amount}
                  tone="auto"
                  sign
                />
              </td>
              <td className="px-3 py-1.5">
                <MovementRoute tx={t} currency={currency} />
              </td>
              <td className="max-w-[24ch] truncate px-3 py-1.5 text-muted" title={t.reason || ""}>
                {t.reason || "—"}
              </td>
              <td className="px-3 py-1.5">{statusLabel(t.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
